import React, { useState, useEffect, useRef } from "react";
import { useLogin } from "state/hooks/useLogin";
import {
  useWebAuthn,
  checkBiometricSupport,
  deviceHasAnyPasskey,
  markEnrolledHere,
  guessDeviceLabel,
} from "state/hooks/useWebAuthn";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCredentials } from "state/authSlice";
import { api } from "state/api";
import { activateAccount, readSavedAccounts } from "state/savedAccounts";
import {
  checkDeviceBiometry,
  getBiometricSession,
  isNativeApp,
  saveBiometricSession,
  setBiometricSignInEnabled,
  verifyIdentity,
} from "native/biometric";
import { extras, light, fontStack } from "styles/tokens";
import logoInverted from "../phieg-logo-inverted.png";
import logoDark from "../phieg-logo.png";
import "./Login.css";

// Token values injected as CSS custom properties (see Login.css) so theme.js
// remains the single source of truth for the brand palette.
const LOGIN_VARS = {
  "--lg-font": fontStack,
  "--lg-surface": light.surface,
  "--lg-text-high": light.textHigh,
  "--lg-text-mid": light.textMid,
  "--lg-border": light.border,
  "--lg-border-strong": light.borderStrong,
  "--lg-accent": extras.accent.main,
  "--lg-accent-hover": extras.accent.hover,
  "--lg-accent-contrast": extras.accent.contrastText,
  "--lg-teal-deep": extras.surface.base,
  "--lg-teal-raised": extras.surface.raised,
  "--lg-danger": extras.feedback.danger,
  "--lg-danger-soft": extras.feedback.dangerSoft,
  "--lg-radius": `${extras.radius.lg}px`,
  "--lg-radius-btn": `${extras.radius.md}px`,
  "--lg-shadow": extras.shadow.lg,
  "--lg-focus": extras.shadow.focusRing,
};

function Login() {
  const { login, error, isLoading, checkError } = useLogin();
  const {
    loginWithPasskey,
    enrollPasskey,
    error: bioError,
    isLoading: bioLoading,
  } = useWebAuthn();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [bioSupported, setBioSupported] = useState(false);
  // Native app only: the Face-ID-protected copy of the last session, if one
  // exists (see native/biometric.js). null = none, so the button offers to
  // set Face ID up instead of signing in with it.
  const [nativeSession, setNativeSession] = useState(null);
  const [nativeLabel, setNativeLabel] = useState("Face ID");
  const [nativeError, setNativeError] = useState(null);
  const [nativeBusy, setNativeBusy] = useState(false);
  // "Set up Face ID" mode: entered when the user taps the Face ID icon on a
  // device with no passkey yet. They sign in with email+password and we enroll
  // a passkey right after — turning on biometric as part of logging in.
  const [bioSetup, setBioSetup] = useState(false);
  // When we enroll-after-login we navigate manually (post-enroll), so this ref
  // tells the password-success effect below to stand down and avoid a double or
  // premature redirect.
  const manualNav = useRef(false);

  // Shared post-login routing for both the password and passkey paths.
  // Bootstrap accounts (no password yet) are sent to /set-password — the
  // App-level guard owns that route; everyone else lands on their redirect
  // target or the dashboard.
  // Accounts still signed in on this device — shown when "Add another
  // account" brought the person here, so they can go back with one tap.
  const [savedAccounts] = useState(() => readSavedAccounts());
  const openSaved = (account) => {
    activateAccount(dispatch, account, { resetApi: api.util.resetApiState });
    navigate("/");
  };

  const goAfterLogin = () => {
    const stored = JSON.parse(localStorage.getItem("user") || "null");
    if (stored?.user?.mustSetPassword) {
      navigate("/set-password");
      return;
    }
    const next = localStorage.getItem("postLoginRedirect");
    if (next) {
      localStorage.removeItem("postLoginRedirect");
      // Legacy value written by the old /notification-preferences page: it
      // hardcoded the Client settings path, which 404s for every other role.
      // Route it through the role-aware entry point instead.
      navigate(next === "/clientsettings" ? "/notification-preferences" : next);
    } else {
      navigate("/dashboard");
    }
  };

  // Show the biometric button only where it can actually work. On the web
  // that is a WebAuthn platform authenticator (passkey sign-in). Inside the
  // native app WebAuthn is unusable (RP-ID is phiegcrm.com, the app's origin
  // is not), so the same button is backed by the device biometric plus a
  // protected copy of the session instead. Local probes only — no network.
  useEffect(() => {
    let active = true;
    if (isNativeApp()) {
      Promise.all([checkDeviceBiometry(), getBiometricSession()]).then(
        ([biometry, session]) => {
          if (!active) return;
          if (biometry.label) setNativeLabel(biometry.label);
          setNativeSession(session);
          setBioSupported(biometry.available || biometry.deviceIsSecure);
        }
      );
    } else {
      checkBiometricSupport().then((ok) => active && setBioSupported(ok));
    }
    return () => {
      active = false;
    };
  }, []);

  // Password auth success is signalled by checkError flipping with no error.
  useEffect(() => {
    if (!error && checkError && !manualNav.current) {
      goAfterLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // In set-up mode we own navigation (after the enroll completes), so the
    // success effect above must not fire first.
    if (bioSetup) manualNav.current = true;
    await login(email, password);

    const stored = JSON.parse(localStorage.getItem("user") || "null");
    if (!stored?.token) {
      manualNav.current = false; // login failed; the error alert shows
      return;
    }
    // Returning user enabling Face ID at sign-in: enroll now, then continue.
    // (Bootstrap accounts have no password yet → they're routed to
    // /set-password, which offers enrollment right after they create it.)
    if (bioSetup && !stored.user?.mustSetPassword) {
      if (isNativeApp()) {
        // In the app "set up Face ID" means switching Face ID sign-in on; that
        // prompts for the biometric, and a protected copy of this session is
        // kept for next time (NativeEvents keeps it fresh).
        const on = await setBiometricSignInEnabled(stored.user?._id, true);
        if (on) await saveBiometricSession(stored);
      } else {
        const enrolled = await enrollPasskey(guessDeviceLabel());
        if (enrolled) markEnrolledHere(stored.user?._id);
      }
    }
    goAfterLogin();
    manualNav.current = false;
  };

  const handleNativeBiometric = async () => {
    if (nativeBusy) return;
    if (!nativeSession) {
      // Nothing to unlock yet — sign in with a password and switch it on.
      setBioSetup(true);
      return;
    }
    setNativeBusy(true);
    setNativeError(null);
    try {
      const ok = await verifyIdentity("Sign in to Phieg");
      if (!ok) return; // cancelled — leave the form as it was
      // Restore exactly what useLogin would have written.
      localStorage.setItem("user", JSON.stringify(nativeSession));
      dispatch(setCredentials(nativeSession));
      goAfterLogin();
    } catch {
      setNativeError(`Couldn't sign in with ${nativeLabel}. Use your password.`);
    } finally {
      setNativeBusy(false);
    }
  };

  const handlePasskey = async () => {
    if (isNativeApp()) {
      await handleNativeBiometric();
      return;
    }
    if (deviceHasAnyPasskey()) {
      const ok = await loginWithPasskey();
      if (ok) goAfterLogin();
    } else {
      // No passkey on this device yet — guide them to sign in and set it up.
      setBioSetup(true);
    }
  };

  const biometricLabel = isNativeApp() ? nativeLabel : "Face ID";
  const biometricBusy = isNativeApp() ? nativeBusy : bioLoading;
  const biometricError = isNativeApp() ? nativeError : bioError;

  return (
    <div className="login-root" style={LOGIN_VARS}>
      <aside className="login-brand" aria-hidden="true">
        <div className="login-brand__inner">
          <img className="login-brand__logo" src={logoInverted} alt="" />
          <p className="login-brand__tagline">
            Lead management for real-estate advertising across Egypt &amp; Dubai.
          </p>
        </div>
        <div className="login-brand__glow" />
      </aside>

      <main className="login-form-pane">
        <div className="login-card">
          {/* Two logo renditions class-toggled by the ≤860px media query — the
              dark mobile card needs the inverted (light) logo and CSS can't
              swap an img src. display:none removes the hidden one from the
              accessibility tree, so both carry the same alt. */}
          <img
            className="login-card__logo login-card__logo--ondark"
            src={logoInverted}
            alt="Phieg"
          />
          <img
            className="login-card__logo login-card__logo--onlight"
            src={logoDark}
            alt="Phieg"
          />
          <h2 className="login-card__title">
            {savedAccounts.length > 0 ? "Add an account" : "Welcome back"}
          </h2>
          <p className="login-card__subtitle">
            {savedAccounts.length > 0
              ? "Sign in to another account. The ones below stay signed in on this device."
              : "Sign in to your account to continue."}
          </p>

          {/* Accounts already signed in on this device (Add another account
              brought the person here): one tap goes back to any of them. */}
          {savedAccounts.length > 0 && (
            <div
              aria-label="Accounts on this device"
              style={{ display: "grid", gap: 8, marginBottom: 16 }}
            >
              {savedAccounts.map((a) => (
                <button
                  key={a.user._id}
                  type="button"
                  onClick={() => openSaved(a)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "var(--lg-radius-btn)",
                    border: "1px solid var(--lg-accent)",
                    background: "transparent",
                    color: "var(--lg-text-high)",
                    font: "inherit",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      border: "1px solid var(--lg-accent)",
                      color: "var(--lg-accent)",
                      fontWeight: 700,
                    }}
                  >
                    {(a.user.name || "?").trim().charAt(0).toUpperCase()}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 600 }}>
                      Continue as {a.user.name}
                    </span>
                    <span style={{ display: "block", fontSize: "0.8rem", opacity: 0.75 }}>
                      {a.user.email || a.user.role}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {error && (
            <div className="login-alert" role="alert">
              <span className="login-alert__icon" aria-hidden="true">
                !
              </span>
              <span>{error.message}</span>
            </div>
          )}

          {bioSetup && (
            <div
              role="status"
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                padding: "12px 14px",
                marginBottom: 16,
                borderRadius: "var(--lg-radius-btn)",
                border: "1px solid var(--lg-accent)",
                background: "transparent",
                color: "var(--lg-text-high)",
                fontSize: "0.9rem",
              }}
            >
              <span
                aria-hidden="true"
                style={{ fontSize: "1.2rem", color: "var(--lg-accent)" }}
              >
                ⚷
              </span>
              <span>
                Sign in with your email and password — we'll set up{" "}
                {biometricLabel} on this device so next time it's one tap.
              </span>
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit}>
            <label className="login-field">
              <span className="login-field__label">Email</span>
              <input
                className="login-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="you@company.com"
                required
              />
            </label>

            <label className="login-field">
              <span className="login-field__label">Password</span>
              <div className="login-input-wrap">
                <input
                  className="login-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="login-reveal"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <span className="login-field__hint">
                First time signing in? Leave the password blank.
              </span>
            </label>

            <button type="submit" className="login-submit" disabled={isLoading}>
              {isLoading
                ? "Signing in…"
                : bioSetup
                ? `Sign in & set up ${biometricLabel}`
                : "Sign in"}
            </button>
          </form>

          {bioSupported && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  margin: "20px 0",
                  color: "var(--lg-text-mid)",
                  fontSize: "13px",
                }}
              >
                <span
                  style={{ flex: 1, height: 1, background: "var(--lg-border)" }}
                />
                or
                <span
                  style={{ flex: 1, height: 1, background: "var(--lg-border)" }}
                />
              </div>
              <button
                type="button"
                onClick={handlePasskey}
                disabled={biometricBusy}
                title={`Sign in with ${biometricLabel}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "var(--lg-radius-btn)",
                  border: "1px solid var(--lg-border-strong)",
                  background: "transparent",
                  color: "var(--lg-accent)",
                  font: "inherit",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: biometricBusy ? "default" : "pointer",
                  opacity: biometricBusy ? 0.55 : 1,
                  transition: "opacity .15s ease, border-color .15s ease",
                }}
              >
                {/* Face ID glyph */}
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 8V6.5A2.5 2.5 0 0 1 6.5 4H8" />
                  <path d="M16 4h1.5A2.5 2.5 0 0 1 20 6.5V8" />
                  <path d="M20 16v1.5a2.5 2.5 0 0 1-2.5 2.5H16" />
                  <path d="M8 20H6.5A2.5 2.5 0 0 1 4 17.5V16" />
                  <path d="M9 10v1" />
                  <path d="M15 10v1" />
                  <path d="M12 10v3h-1" />
                  <path d="M9.5 15.4c.7.6 1.6.9 2.5.9s1.8-.3 2.5-.9" />
                </svg>
                Sign in with {biometricLabel}
              </button>
              {biometricError && (
                <div
                  className="login-alert"
                  role="alert"
                  style={{ marginTop: 12 }}
                >
                  <span className="login-alert__icon" aria-hidden="true">
                    !
                  </span>
                  <span>{biometricError}</span>
                </div>
              )}
            </>
          )}

          {/* Restored now that a reset process exists: /forgot-password mails
              a single-use, 30-minute link. (It was deliberately absent while
              passwords were admin-managed only.) */}
          <Link className="login-forgot" to="/forgot-password">
            Forgot your password?
          </Link>
        </div>
      </main>
    </div>
  );
}

export default Login;
